import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  Alert,
  Paper,
  TextField,
  IconButton,
  FormControlLabel,
  InputAdornment,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import MSwitch from "../common/switch";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { toEntityTitleSubtitle } from "../common/string-helpers";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const EditGroupDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  groupName,
  setGroupName,
  entities,
  selectedEntities,
  onToggleEntity,
  isNewGroup = true,
  meta,
}) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const [filterText, setFilterText] = useState("");
  const [nameError, setNameError] = useState(false);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [isRunning, setIsRunning] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");

  // Get unavailable entities and their count
  const unavailableEntities = useMemo(() => {
    const availableIds = entities.map(
      (entity) => `${entity.integration}-${entity.id}`
    );
    return selectedEntities.filter((id) => !availableIds.includes(id));
  }, [entities, selectedEntities]);

  const unavailableCount = unavailableEntities.length;

  const handleClose = () => {
    setFilterText("");
    setNameError(false);
    onClose();
  };

  const handleSave = async () => {
    setIsRunning("SAVE");
    if (!groupName.trim()) {
      setNameError(true);
      setIsRunning("");
      return;
    }

    // Uncheck all unavailable entities before saving
    const updatedSelection = selectedEntities.filter((entityId) => {
      const availableIds = entities.map(
        (entity) => `${entity.integration}-${entity.id}`
      );
      return availableIds.includes(entityId);
    });

    setFilterText("");
    setNameError(false);

    try {
      await onSave(updatedSelection);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsRunning("");
    }
  };

  const handleNameChange = (e) => {
    setGroupName(e.target.value);
    if (nameError) {
      setNameError(false);
    }
  };

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setIsRunning("DELETE");
    if (onDelete) {
      try {
        await onDelete();
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        setDeleteConfirmOpen(false);
        setIsRunning("");
      }
    }
  };

  const filteredEntities = entities.filter((entity) => {
    const matchesFilter = entity.name
      .toLowerCase()
      .includes(filterText.toLowerCase());
    const isSelected = selectedEntities.includes(
      `${entity.integration}-${entity.id}`
    );
    return showSelectedOnly ? isSelected && matchesFilter : matchesFilter;
  });

  const handleToggleAll = () => {
    const filteredEntityIds = filteredEntities.map(
      (entity) => `${entity.integration}-${entity.id}`
    );

    const allFilteredSelected = filteredEntityIds.every((id) =>
      selectedEntities.includes(id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered entities
      filteredEntityIds.forEach((id) => {
        if (selectedEntities.includes(id)) {
          onToggleEntity(id);
        }
      });
    } else {
      // Select all filtered entities
      filteredEntityIds.forEach((id) => {
        if (!selectedEntities.includes(id)) {
          onToggleEntity(id);
        }
      });
    }
  };

  const isIndeterminate =
    filteredEntities.some((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    ) &&
    !filteredEntities.every((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    );

  const isChecked =
    filteredEntities.length > 0 &&
    filteredEntities.every((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isSmallScreen}
        variant={isSmallScreen ? undefined : "drawer"}
        PaperProps={{
          style: {
            width: isSmallScreen ? "100%" : "600px",
            marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
            marginBottom: isSmallScreen
              ? 0
              : `${safeAreaInsets.bottom}px !important`,
            height: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            maxHeight: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
            paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
            paddingRight: `${safeAreaInsets.right}px`,
          },
        }}
      >
        <DialogTitle className="gap-2 flex items-center min-h-0">
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="flex-shrink-0"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            className="leading-tight mb-0"
          >
            {isNewGroup ? "Create Group" : "Edit Group"}
          </Typography>
        </DialogTitle>

        <DialogContent className="flex flex-col px-4 pb-4 gap-4 overflow-hidden">
          <Paper
            variant="outlined"
            className="p-4"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              variant="outlined"
              onChange={handleNameChange}
              error={nameError}
              helperText={nameError ? "Group name is required" : ""}
              size="small"
            />
          </Paper>

          {unavailableCount > 0 && (
            <Alert severity="warning" className="py-1">
              {unavailableCount}{" "}
              {unavailableCount === 1 ? "entity is" : "entities are"} no longer
              available. They will be automatically removed upon saving.
            </Alert>
          )}

          <Paper
            variant="outlined"
            className="flex flex-col flex-1 overflow-hidden"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <div className="p-4 pb-2">
              <TextField
                fullWidth
                size="small"
                placeholder="Search entities..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div
              className="flex items-center justify-between mx-4 py-2 border-b"
              style={{
                borderColor: "hsl(var(--border))",
              }}
            >
              <div className="flex items-center">
                <MSwitch
                  checked={showSelectedOnly}
                  onChange={(e) => setShowSelectedOnly(e.target.checked)}
                  size="small"
                  className="mr-2"
                />
                <Typography variant="body2">Selected Only</Typography>
              </div>
              <div className="flex items-center">
                <Checkbox
                  edge="start"
                  indeterminate={isIndeterminate}
                  checked={isChecked}
                  onChange={handleToggleAll}
                />
                <Typography variant="body2" className="ml-1">
                  Select All
                </Typography>
              </div>
            </div>

            <List className="w-full overflow-auto flex-1">
              {filteredEntities.map((entity) => {
                const entityId = `${entity.integration}-${entity.id}`;
                const isSelected = selectedEntities.includes(entityId);

                // Get entity title and subtitle using the same helper as EntityCard
                const entityInfo = toEntityTitleSubtitle(entity.name, true);
                const displayTitle =
                  typeof entityInfo === "object"
                    ? entityInfo.title
                    : entityInfo;
                const displaySubtitle =
                  typeof entityInfo === "object"
                    ? entityInfo.subtitle
                    : meta?.integrationNames?.[entity.integration] ||
                      entity.integration;

                return (
                  <ListItem
                    key={entityId}
                    onClick={() => onToggleEntity(entityId)}
                    className="rounded-2xl py-1 cursor-pointer"
                    style={{
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <ListItemIcon className="min-w-[40px]">
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          className="font-medium text-sm leading-tight line-clamp-2"
                        >
                          {displayTitle}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          className="mt-1"
                        >
                          {displaySubtitle}
                        </Typography>
                      }
                      className="m-0"
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions className="px-4 pt-2 pb-6">
          {!isNewGroup && onDelete && (
            <Button
              onClick={handleDelete}
              color="error"
              disabled={isRunning !== ""}
              className="mr-auto normal-case"
              variant="outlined"
              startIcon={<DeleteIcon />}
            >
              Delete Group
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={isRunning !== ""}
            variant="outlined"
            className="normal-case"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!groupName.trim() || isRunning !== ""}
            className="normal-case"
            style={{ boxShadow: "none" }}
          >
            Save
            {isRunning === "SAVE" && (
              <CircularProgress size={18} className="ml-2" />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        variant="pops"
      >
        <DialogTitle className="p-6 flex items-center gap-2 min-h-[48px]">
          <Typography variant="subtitle1">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent className="p-4 mt-2">
          <Typography variant="body2">
            Are you sure you want to delete this group?
          </Typography>
        </DialogContent>
        <DialogActions className="px-4 pb-4">
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            className="normal-case"
            style={{ boxShadow: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isRunning === "DELETE"}
            className="normal-case"
            style={{ boxShadow: "none" }}
          >
            Delete
            {isRunning === "DELETE" && (
              <CircularProgress size={18} className="ml-2" />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditGroupDialog;
